import React from 'react';
import PropTypes from 'prop-types';
import { prop, assoc } from 'ramda';

import { Document, Page, View, Text } from '@react-pdf/renderer';
import Header from '../Header';

import { convertToBrl } from 'helpers/converters';
import { formatServicesCategories } from 'helpers/formatters';

import { styles } from '../styles';

const col3 = { width: `${100 / 3}%` };
const col4 = { width: `${100 / 4}%` };
const col5 = { width: `${100 / 5}%` };

const CashFlow = ({ cashFlow }) => {
  return(
    <View style={{ marginBottom: 10 }}>
      <View>
        <Text style={[styles.financialResumeTitle, { marginTop: 10, marginBottom: 10 }]}>
          Fluxo de caixa
        </Text>
      </View>
      <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <View style={styles.financialResumeSuccess}>
          <Text style={styles.financialResumeBoxTitle}>
            Receita
          </Text>
          <Text style={styles.financialResumeBoxValue}>
            {convertToBrl(+cashFlow.entry)}
          </Text>
        </View>
        <View style={styles.financialResumeDanger}>
          <Text style={styles.financialResumeBoxTitle}>
            Despesa
          </Text>
          <Text style={styles.financialResumeBoxValue}>
            {convertToBrl(+cashFlow.exit)}
          </Text>
        </View>
        <View style={styles.financialResumePrimary}>
          <Text style={styles.financialResumeBoxTitle}>
            Lucro
          </Text>
          <Text style={styles.financialResumeBoxValue}>
            {convertToBrl(+cashFlow.balance)}
          </Text>
        </View>
      </View>
    </View>
  );
}

const Resume = ({ data, name, type, hasPercentage = true, isProduct = false, isServices = false }) => {
  if(!data) {
    return null;
  }

  return(
    <View>
      <View>
        <Text style={[styles.financialResumeTitle, { marginBottom: 5 }]}>
          {name}
        </Text>
      </View>
      <View style={styles.tableFinancial}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancial, hasPercentage ? col4 : col3]}>
            <Text style={styles.tableCellHeaderFinancial}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, hasPercentage ? col4 : col3]}>
            <Text style={styles.tableCellHeaderFinancial}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, hasPercentage ? col4 : col3]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantidade</Text>
          </View>
          {hasPercentage && (
            <View style={[styles.tableColHeaderFinancial, hasPercentage ? col4 : col3]}>
              <Text style={styles.tableCellHeaderFinancial}>Percentual (%)</Text>
            </View>
          )}
        </View>
        {data.content.map(resume => (
          <View style={styles.tableRowFinancial}>
            <View style={[styles.tableColFinancial, hasPercentage ? col4 : col3]}>
              {isProduct ? (
                <Text style={styles.tableCellFinancial}>
                  {resume.name}
                </Text>
              ) : (
                <>
                  {isServices ? (
                    <View>
                      <Text style={[styles.tableCellFinancial, { marginBottom: 0 }]}>
                        {resume?.description}
                      </Text>
                      {(resume?.categories != '-') && (
                        <Text style={[styles.tableCellFinancial, styles.fontSize6]}>
                          Categoria(s): {resume?.categories}
                        </Text>
                      )}
                    </View>
                  ) : (
                    <Text style={styles.tableCellFinancial}>{prop(type, resume)}</Text>
                  )}
                </>
              )}
            </View>
            <View style={[styles.tableColFinancial, hasPercentage ? col4 : col3]}>
              <Text style={styles.tableCellFinancial}>
                {convertToBrl(+resume.totalAmount)}
              </Text>
            </View>
            <View style={[styles.tableColFinancial, hasPercentage ? col4 : col3]}>
              <Text style={styles.tableCellFinancial}>{resume.total}</Text>
            </View>
            {hasPercentage && (
              <View style={[styles.tableColFinancial, hasPercentage ? col4 : col3]}>
                <Text style={styles.tableCellFinancial}>{resume.percent}%</Text>
              </View>
            )}
          </View>
        ))}
      </View>
    </View>
  );
}

const PaymentMethods = ({ data, name }) => {
  const { serviceOrders, services, invoices, manual, orders } = data;

  const createPaymentMethod = (type, name) => {
    if(!type) {
      return null;
    }

    const titleStyle = index => {
      if(index === 0) {
        return [styles.tableColFinancial, col5, { borderBottomWidth: 0 }];
      }

      if(index > 0 && index < type.content.length - 1) {
        return [
          styles.tableColFinancial,
          col5,
          { borderTopWidth: 0, borderBottomWidth: 0 }
        ];
      }

      return [styles.tableColFinancial, col5, { borderTopWidth: 0 }];
    }

    return type.content.map((resume, index) => (
      <View style={styles.tableRowFinancial}>
        <View style={[
          titleStyle(index),
          index === (type.content.length - 1) ? styles.tableCellBorderBottomFinancial : ''
        ]}>
          <Text style={styles.tableCellFinancial}>{index === 0 ? name : ''}</Text>
        </View>
        <View style={[styles.tableColFinancial, col5]}>
          <Text style={styles.tableCellFinancial}>{resume.paymentMethodName}</Text>
        </View>
        <View style={[styles.tableColFinancial, col5]}>
          <Text style={styles.tableCellFinancial}>
            {convertToBrl(+resume.totalAmount)}
          </Text>
        </View>
        <View style={[styles.tableColFinancial, col5]}>
          <Text style={styles.tableCellFinancial}>{resume.total}</Text>
        </View>
        <View style={[styles.tableColFinancial, col5]}>
          <Text style={styles.tableCellFinancial}>{resume.percent}%</Text>
        </View>
      </View>
    ));
  }

  return(
    <View>
      <View>
        <Text style={[styles.financialResumeTitle, { marginBottom: 5 }]}>
          {name}
        </Text>
      </View>
      <View style={styles.tableFinancial}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Tipo</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantidade</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col5]}>
            <Text style={styles.tableCellHeaderFinancial}>Percentual (%)</Text>
          </View>
        </View>
        {createPaymentMethod(serviceOrders, 'Rotativo')}
        {createPaymentMethod(services, 'Serviços')}
        {createPaymentMethod(orders, 'Pedidos')}
        {createPaymentMethod(invoices, 'Faturas')}
        {createPaymentMethod(manual, 'Movimentações avulsas')}
      </View>
    </View>
  );
}

const Invoices = ({ data, name }) => {
  const { paidOut } = data.content;
  const invoices = [assoc('label', 'Pagas', paidOut)];

  if(data.total === 0) {
    return false;
  }

  return(
    <View>
      <View>
        <Text style={[styles.financialResumeTitle, { marginBottom: 5 }]}>
          {name}
        </Text>
      </View>
      <View style={styles.tableFinancial}>
        <View style={styles.tableRowFinancial}>
          <View style={[styles.tableColHeaderFinancial, col4]}>
            <Text style={styles.tableCellHeaderFinancial}>Descrição</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col4]}>
            <Text style={styles.tableCellHeaderFinancial}>Valor</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col4]}>
            <Text style={styles.tableCellHeaderFinancial}>Quantidade</Text>
          </View>
          <View style={[styles.tableColHeaderFinancial, col4]}>
            <Text style={styles.tableCellHeaderFinancial}>Percentual (%)</Text>
          </View>
        </View>
        {invoices.map(invoice =>
          invoice.totalAmount !== 0 && (
            <View style={styles.tableRowFinancial}>
              <View style={[styles.tableColFinancial, col4]}>
                <Text style={styles.tableCellFinancial}>{invoice.label}</Text>
              </View>
              <View style={[styles.tableColFinancial, col4]}>
                <Text style={styles.tableCellFinancial}>
                  {convertToBrl(+invoice.totalAmount)}
                </Text>
              </View>
              <View style={[styles.tableColFinancial, col4]}>
                <Text style={styles.tableCellFinancial}>{invoice.total}</Text>
              </View>
              <View style={[styles.tableColFinancial, col4]}>
                <Text style={styles.tableCellFinancial}>{invoice.percent}%</Text>
              </View>
            </View>
          )
        )}
      </View>
    </View>
  );
}

const FinancialResume = ({
  exportData,
  dateRange,
  fileName,
  establishmentName
}) => {
  const {
    cashFlow,
    serviceOrders,
    services,
    products,
    reservations,
    entryCategory,
    exitCategory,
    invoices,
    paymentMethods,
    discounts,
    servicesCategories
  } = exportData;

  const servicesWithCategories = services?.content?.map(service => {
    return {
      ...service,
      categories: formatServicesCategories(service?.serviceCategories, servicesCategories)
    }
  });

  const formattedServices = {
    ...services,
    content: servicesWithCategories
  }

  return(
    <Document>
      <Page style={styles.body}>
        <Header
          fileName={fileName}
          dateRange={dateRange}
          establishmentName={establishmentName}
        />
        <CashFlow cashFlow={cashFlow} />
        {+discounts?.totalAmount != 0 && (
          <Resume
            name="Descontos"
            type="description"
            data={discounts}
            hasPercentage={false}
          />
        )}
        {serviceOrders?.totalAmount > 0 && (
          <Resume
            name="Rotativos"
            type="typePrice"
            data={serviceOrders}
          />
        )}
        {services?.content?.length > 0 && (
          <Resume
            name="Serviços"
            type="description"
            data={formattedServices}
            isServices
          />
        )}
        {products?.content?.length > 0 && (
          <Resume
            name="Produtos"
            type="description"
            data={products}
            hasPercentage={false}
            isProduct
          />
        )}
        <Resume
          name="Reservas"
          type="reservationTypeName"
          data={reservations}
        />
        <Resume
          name="Movimentações de entrada"
          type="categoryName"
          data={entryCategory}
        />
        <Resume
          name="Movimentações de saída"
          type="categoryName"
          data={exitCategory}
        />
        <Invoices
          data={invoices}
          name="Faturas"
        />
        <PaymentMethods
          data={paymentMethods}
          name="Meios de pagamento"
        />
      </Page>
    </Document>
  );
}

FinancialResume.propTypes = {
  exportData: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  fileName: PropTypes.string.isRequired,
  establishmentName: PropTypes.string.isRequired
};

CashFlow.propTypes = {
  cashFlow: PropTypes.shape({
    entry: PropTypes.oneOf([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired
    ]).isRequired,
    exit: PropTypes.oneOf([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired
    ]).isRequired,
    balance: PropTypes.oneOf([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired
    ]).isRequired
  }).isRequired
};

Resume.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired
};

PaymentMethods.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  name: PropTypes.string.isRequired
};

Invoices.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape.isRequired).isRequired,
  name: PropTypes.string.isRequired
};

export default FinancialResume;